import React, { useMemo, useState } from 'react';
import { analyzePassword, passwordsMatch } from '../utils/passwordUtils';

const inputClass =
  'w-full border border-slate-300 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500';

function RuleItem({ met, label }) {
  return (
    <li className={`flex items-center gap-2 text-xs ${met ? 'text-green-700' : 'text-slate-500'}`}>
      <span
        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
          met ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'
        }`}
        aria-hidden
      >
        {met ? '✓' : '·'}
      </span>
      {label}
    </li>
  );
}

const PasswordFields = ({
  password,
  confirmPassword,
  email = '',
  onPasswordChange,
  onConfirmChange,
  autoFocus = false,
  passwordLabel = 'Password',
  confirmLabel = 'Confirm password',
  passwordPlaceholder = 'Create a strong password',
  confirmPlaceholder = 'Re-enter your password',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const analysis = useMemo(() => analyzePassword(password, email), [password, email]);
  const matched = passwordsMatch(password, confirmPassword);
  const confirmTouched = confirmPassword.length > 0;

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="password">
          {passwordLabel}
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            className={inputClass}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder={passwordPlaceholder}
            autoComplete="new-password"
            autoFocus={autoFocus}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-800"
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>

        {password.length > 0 && (
          <div className="mt-3" aria-live="polite">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-slate-500">Password strength</span>
              <span
                className={`font-medium ${
                  analysis.strengthLabel === 'Strong'
                    ? 'text-green-700'
                    : analysis.strengthLabel === 'Good'
                      ? 'text-teal-700'
                      : analysis.strengthLabel === 'Fair'
                        ? 'text-amber-700'
                        : 'text-red-600'
                }`}
              >
                {analysis.strengthLabel}
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${analysis.strengthColor}`}
                style={{ width: `${analysis.strength}%` }}
              />
            </div>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {analysis.rules.map((rule) => (
                <RuleItem key={rule.id} met={rule.met} label={rule.label} />
              ))}
            </ul>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="password_confirm">
          {confirmLabel}
        </label>
        <div className="relative">
          <input
            id="password_confirm"
            type={showConfirm ? 'text' : 'password'}
            className={`${inputClass} ${
              confirmTouched
                ? matched
                  ? 'border-green-400 focus:ring-green-500 focus:border-green-500'
                  : 'border-red-300 focus:ring-red-400 focus:border-red-400'
                : ''
            }`}
            value={confirmPassword}
            onChange={(e) => onConfirmChange(e.target.value)}
            placeholder={confirmPlaceholder}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowConfirm((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-800"
          >
            {showConfirm ? 'Hide' : 'Show'}
          </button>
        </div>
        {confirmTouched && (
          <p className={`text-xs mt-1.5 ${matched ? 'text-green-700' : 'text-red-600'}`}>
            {matched ? 'Passwords match' : 'Passwords do not match'}
          </p>
        )}
      </div>
    </div>
  );
};

export default PasswordFields;

export { analyzePassword, passwordsMatch };
